import {Injectable} from "@angular/core";
import {CredentialsStorage} from "@app/core/credentials/domain/credentials-storage";
import {DeliveryPointLocal} from "@app/core/delivery-point/domain/delivery-point.local";
import {ApiUrls} from "@app/core/shared/infrastructure/api-urls";
import {BaseHandleErrorMapper} from "@app/core/shared/infrastructure/base.handler/base-handle-error.mapper";
import {HandlerBuilder, HttpMethod} from "@app/core/shared/infrastructure/base.handler/base-handle.parameters";
import {EuropastryBaseHandler} from "@app/core/shared/infrastructure/base.handler/europastry-base.handler";
import {Observable} from "rxjs";
import {catchError, map, switchMap} from "rxjs/operators";
import {CouponRemote} from "../domain/coupon.remote";
import {Coupon} from "../domain/dtos/coupon";
import {CouponListPage} from "../domain/dtos/coupon-list-page";
import {CouponListPageMapper} from "./mappers/coupon-list-page.mapper";
import {CouponMapper} from "./mappers/coupon.mapper";
import {CartLine} from "@app/core/cart/domain/dtos/cart-line";
import {ValidateCouponErrorMapper} from "@app/core/coupon/infrastructure/mappers/validate-coupon-error.mapper";


@Injectable({
    providedIn: "root",
})

export class CouponWebService implements CouponRemote {

    constructor(
        private baseHandler: EuropastryBaseHandler,
        private deliveryPointLocal: DeliveryPointLocal,
        private credentialsStorage: CredentialsStorage,
        private apiUrls: ApiUrls,
    ) { }

    validateCouponByReference(couponCode: string, cart: CartLine[], recaptchaToken: string): Observable<Coupon> {
        const cartLines=cart.map(obj => ({code: obj.code, quantity: obj.quantity}))

        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {
            const parameters = new HandlerBuilder()
                .method(HttpMethod.post)
                .body({
                    cart: cartLines,
                    recaptchaToken: recaptchaToken
                })
                .authorization(this.credentialsStorage.getCredentials())
                .url(this.apiUrls.VALIDATE_COUPON + deliveryPointCode + "/" + couponCode + "/references")
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map(CouponMapper.deserialize),
                catchError(error => ValidateCouponErrorMapper.handleCouponError(error))
            );
        }));
    }

    getAvailableCoupons(itemsPerPage: number, page: number): Observable<CouponListPage> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .authorization(this.credentialsStorage.getCredentials())
                .url(this.apiUrls.AVAILABLE_COUPONS + deliveryPointCode + "/" + itemsPerPage + "/" + page)
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map(CouponListPageMapper.deserialize),
                catchError(BaseHandleErrorMapper.handleError)
            );
        }));
    }

    getUsedCoupons(itemsPerPage: number, page: number): Observable<CouponListPage> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .authorization(this.credentialsStorage.getCredentials())
                .url(this.apiUrls.USED_COUPONS + deliveryPointCode + "/" + itemsPerPage + "/" + page)
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map(CouponListPageMapper.deserialize),
                catchError(BaseHandleErrorMapper.handleError)
            );
        }));
    }

    getAccumulatedAmount(): Observable<number> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .authorization(this.credentialsStorage.getCredentials())
                .url(this.apiUrls.ACCUMULATED_AMOUNT + deliveryPointCode)
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map(response => response.results),
                catchError(BaseHandleErrorMapper.handleError)
            );
        }));
    }

}
